import React from 'react'
import PropTypes from 'prop-types'
import * as Types from 'types'
import exact from 'prop-types-exact'
import { FileInput, DocumentPreview, DocumentRemove } from 'components'
import { destroyFile } from 'api'
import { Spinner } from 'lp-components'
import { FILE_SIZE_TEXT, MAX_IMAGE_SIZE_TO_RESIZE, MAX_FILE_IMAGE_WIDTH, MAX_FILE_IMAGE_HEIGHT } from 'config'
import classNames from 'classnames'

const propTypes = {
  uploadDetails: Types.uploadDetails.isRequired,
  name: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  mainHeadingLevel: PropTypes.number,
  hideMainHeading: PropTypes.bool,
  children: PropTypes.node,
}

const defaultProps = {
  isLoading: false,
  mainHeadingLevel: 6,
  hideMainHeading: false,
  children: null,
}

function DocumentUploads({
  uploadDetails,
  name,
  isLoading,
  mainHeadingLevel,
  hideMainHeading,
  children,
}) {
  const { uploadHeader, uploadSubHeader } = uploadDetails

  const handleFileDelete = async (fileContentDocumentId) => {
    await destroyFile(fileContentDocumentId)
  }
  const inputHeaderId = `${name}-header`
  const Heading = `h${mainHeadingLevel}`

  async function compressFile(file) {
    if (!file.type.startsWith('image/')) {
      return file.url
    }

    // if size is greater than 10MB, resize the image
    if (file.size > MAX_IMAGE_SIZE_TO_RESIZE) {
      return await resizeImage(file)
    }

    return file.url
  }

  function resizeImage(file) {
    const img = new Image()
    img.src = file.url

    return new Promise((resolve) => {
      // Ref: https://stackoverflow.com/questions/14672746/how-to-compress-an-image-via-javascript-in-the-browser
      img.onload = () => {
        const canvas = document.createElement('canvas');
      
        const max_width = MAX_FILE_IMAGE_WIDTH; // downscales a 30MB to a 8MB image
        const max_height = MAX_FILE_IMAGE_HEIGHT;
        let width = img.width;
        let height = img.height;
      
        // calculate the width and height, constraining the proportions
        if (width > height) {
          if (width > max_width) {
            height = Math.round(height * max_width / width);
            width = max_width;
          }
        } else {
          if (height > max_height) {
            width = Math.round(width * max_height / height);
            height = max_height;
          }
        }
        
        // resize the canvas and draw the image data into it
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        
        resolve(canvas.toDataURL("image/jpeg", 0.7)); // get the data from canvas as 70% JPG (can be also PNG, etc.)
      }
    })
  
  }


  return (
    <div className="flex-section">
      <div>
        <div className="document-heading">
          {!hideMainHeading && <Heading>Upload Document(s)</Heading>}
          <p className="file-type" id={inputHeaderId}>
            {uploadHeader}
          </p>
        </div>
        <p
          className={classNames({ 'restrict-width': hideMainHeading })}
          dangerouslySetInnerHTML={{ __html: uploadSubHeader }}
        />
        <p className="tiny">{FILE_SIZE_TEXT}</p>
        {children}
      </div>
      {isLoading ? (
        <Spinner />
      ) : (
        <FileInput
          name={name}
          label={false}
          aria-labelledby={inputHeaderId}
          multiple={true}
          selectText="Choose File(s)"
          onChange={async (e) => {
            e.target.value.forEach(async (file) => {
              const compressedFile = await compressFile(file)
              file.url = compressedFile
              return file
            })
          }}
          onRemove={(event) => {
            if (!event.contentDocumentId) return
            handleFileDelete(event.contentDocumentId)
          }}
          previewComponent={(props) => <DocumentPreview {...props} />}
          removeComponent={(props) => <DocumentRemove {...props} />}
        />
      )}
    </div>
  )
}

DocumentUploads.propTypes = exact(propTypes)
DocumentUploads.defaultProps = defaultProps

export default React.memo(DocumentUploads)
